// DAS-2147: "Your Risk" modal for PortfolioAnalysisView
// Shows per-ticker and portfolio-level risk metrics with full transparency
// about data sources, calculation methods, and known limitations.

import React, { useState, useMemo } from 'react';
import {
  Box, Typography, Paper, Tooltip, Chip, Collapse, IconButton,
  Table as MuiTable, TableHead, TableBody, TableRow, TableCell,
  TableContainer, TableSortLabel, TextField, InputAdornment,
  Alert, AlertTitle, Divider,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ClickableTickerChip from './ClickableTickerChip';

// ─── Formatters ─────────────────────────────────────────────────

const fmtPct = (v, decimals = 2) => {
  if (v == null || isNaN(v)) return '—';
  return `${Number(v).toFixed(decimals)}%`;
};

const fmtNum = (v, decimals = 2) => {
  if (v == null || isNaN(v)) return '—';
  return Number(v).toFixed(decimals);
};

// ─── Level classifiers ──────────────────────────────────────────

const SHARPE_LEVELS = [
  { min: 1.5, label: 'Excellent', color: '#1b5e20' },
  { min: 1.0, label: 'Good', color: '#2e7d32' },
  { min: 0.5, label: 'Adequate', color: '#f57c00' },
  { min: 0, label: 'Poor', color: '#e65100' },
  { min: -Infinity, label: 'Negative', color: '#c62828' },
];

const BETA_LEVELS = [
  { max: 0.8, label: 'Defensive', color: '#2e7d32' },
  { max: 1.2, label: 'Market-like', color: '#1976d2' },
  { max: 1.5, label: 'Aggressive', color: '#f57c00' },
  { max: Infinity, label: 'High Beta', color: '#c62828' },
];

const PROB_LEVELS = [
  { min: 65, label: 'Favorable', color: '#1b5e20' },
  { min: 55, label: 'Leaning positive', color: '#2e7d32' },
  { min: 45, label: 'Coin flip', color: '#f57c00' },
  { min: -Infinity, label: 'Unfavorable', color: '#c62828' },
];

const getSharpeLevel = (v) => SHARPE_LEVELS.find(l => v >= l.min) || SHARPE_LEVELS[SHARPE_LEVELS.length - 1];
const getBetaLevel = (v) => BETA_LEVELS.find(l => v <= l.max) || BETA_LEVELS[BETA_LEVELS.length - 1];
const getProbLevel = (v) => PROB_LEVELS.find(l => v >= l.min) || PROB_LEVELS[PROB_LEVELS.length - 1];

// ─── Data source documentation ──────────────────────────────────

const DATA_SOURCES = {
  beta: {
    label: 'Beta',
    source: 'YF_Data (yfinance)',
    what: '5-year monthly beta vs S&P 500. Measures how much a stock moves relative to the broad US market.',
    meaning: 'Beta 1.0 = moves with the market. >1 = amplifies market moves. <1 = dampens them. It tells you: "If the market drops 10%, this stock historically drops ~beta×10%."',
    limitation: 'Benchmark is S&P 500, which may not be appropriate for non-US stocks. Past beta doesn\'t guarantee future behavior.',
    portfolio: 'Weighted avg is shown. This IS approximately valid for beta (it\'s roughly linear/additive).',
  },
  sharpe: {
    label: 'Sharpe Ratio',
    source: 'Ticker_Performance_Data (DR_sharpe_ratio.py)',
    what: 'Trailing 1 year of daily close prices from yfinance. Sharpe = (annualized return − risk-free rate) / annualized volatility. Risk-free rate is exchange-specific (e.g. US 10Y Treasury for NYSE, German Bund for Xetra).',
    meaning: 'How much return you got per unit of total risk. >1.0 is good (decent reward for the risk). >1.5 is excellent. <0 means the stock lost money. It answers: "Was the return worth the rollercoaster?"',
    limitation: 'Per-ticker only. A weighted average of individual Sharpes ≠ portfolio Sharpe (that requires correlations between all positions, which we don\'t compute here).',
    portfolio: 'Weighted avg is shown as a rough indication, not a true portfolio Sharpe.',
  },
  returnProb: {
    label: 'P(Return+)',
    source: 'Ticker_Performance_Data (DC_Ticker_Return_Probability.py)',
    what: 'Uses a fundamentals-based model: expected quarterly return = historical sector base return (for current market regime) + alpha estimated from ~15 fundamental factors. P(positive) = Φ(expected_return / residual_vol), where residual_vol = sector quarterly volatility × √(1−R²) and R = composite fundamental correlation.',
    meaning: 'Fundamentals-strength ranking shown as a percentage — higher = stronger vs sector peers. Not a literal next-quarter probability; the proven odds are at one year (best fifth: 59% higher a year later, worst fifth: 39%).',
    limitation: 'This is a model output, not a guarantee. Depends on which fundamental fields have data, sector classification, and regime detection. Coverage varies per ticker.',
    portfolio: 'Weighted avg is shown. Diversification effects are not captured.',
  },
  expectedReturn: {
    label: 'Exp. Q Return',
    source: 'Ticker_Performance_Data (DC_Ticker_Return_Probability.py)',
    what: 'Expected quarterly return (%) = base sector return for current regime + alpha from fundamental percentiles.',
    meaning: 'Relative score combining the sector baseline with stock-specific fundamental quality. Positive = stronger than the baseline. For comparing stocks; the proven signal is at the one-year horizon.',
    limitation: 'Model estimate, not a forecast. Alpha is based on historical correlations between fundamental percentiles and subsequent returns.',
  },
  residualVol: {
    label: 'Residual Vol (Q)',
    source: 'Ticker_Performance_Data (DC_Ticker_Return_Probability.py)',
    what: 'Quarterly sector volatility × √(1−R²), where R = composite correlation from fundamental factors. This is the portion of quarterly return variance NOT explained by the fundamental model.',
    meaning: 'Think of it as "uncertainty": after the model has factored in fundamentals, how much unpredictable quarterly swing remains. Lower = the model explains more of the stock\'s behavior.',
    limitation: 'This is NOT total volatility and NOT annualized. Do not compare it to standard "annualized volatility" figures.',
  },
  riskAdjReturn: {
    label: 'Risk-Adj Q',
    source: 'Ticker_Performance_Data (derived)',
    what: 'Expected quarterly return / residual volatility. A quarterly Sharpe-like efficiency ratio.',
    meaning: 'How much expected return you get per unit of unexplained risk. Higher = better conviction. Negative = expected to lose money.',
    limitation: 'Different from the annual Sharpe ratio — uses a different volatility concept (residual vs total) and time horizon (quarterly vs annual).',
  },
  drawdown: {
    label: 'Recent Drawdown',
    source: 'Ticker_Performance_Data (6M and YTD performance)',
    what: 'Worst of 6-month and year-to-date return for each ticker.',
    meaning: 'Which position has been hit the hardest recently. This isn\'t a formal max drawdown calculation (peak-to-trough), but it highlights your biggest recent pain point.',
    limitation: 'Based on period returns, not actual peak-to-trough drawdown measurement.',
  },
  portfolioVol: {
    label: 'Portfolio Volatility',
    source: 'Portfolio_Risk_Metrics (DC_Portfolio_Risk_Metrics.py)',
    what: 'Downloads 1 year of daily close prices from yfinance for all portfolio tickers. Computes weighted portfolio daily returns using current position weights. Annualized volatility = std(daily portfolio returns) × √252.',
    meaning: 'How much your overall portfolio value swings. 15% means in a typical year, your portfolio fluctuates roughly ±15%. Lower = smoother ride. Crucially, this captures diversification — if your stocks don\'t all move together, portfolio vol is LOWER than the average individual vol.',
    limitation: 'Uses current weights applied to historical prices (assumes you held these weights all year). Only covers tickers with yfinance price data — check coverage %.',
  },
  portfolioVar: {
    label: 'Value at Risk',
    source: 'Portfolio_Risk_Metrics (DC_Portfolio_Risk_Metrics.py)',
    what: 'VaR = daily portfolio volatility × Z-score × covered portfolio value. Z=1.645 for 95%, Z=2.326 for 99%. 5-day VaR = 1-day VaR × √5. Computed on the portion of the portfolio with price data.',
    meaning: 'On 19 out of 20 trading days, the covered portion of your portfolio should not lose more than this amount. On that 1-in-20 bad day, losses could exceed this.',
    limitation: 'Parametric (assumes normally distributed returns). Real markets have fat tails — actual worst days can be worse. EUR amounts are based on covered value only, not full portfolio.',
  },
  portfolioSharpe: {
    label: 'Portfolio Sharpe',
    source: 'Portfolio_Risk_Metrics (DC_Portfolio_Risk_Metrics.py)',
    what: 'Computed from actual portfolio daily return series: Sharpe = (annualized portfolio return − risk-free rate) / annualized portfolio volatility. Risk-free rate: 4% (approximate).',
    meaning: 'How much return your portfolio earned per unit of risk, measured from actual combined returns. Unlike per-ticker Sharpe averages, this IS a true portfolio Sharpe — it naturally captures diversification and correlation effects.',
    limitation: 'Based on last ~1 year of data. Uses current weights retroactively.',
  },
  maxDrawdown: {
    label: 'Max Drawdown',
    source: 'Portfolio_Risk_Metrics (DC_Portfolio_Risk_Metrics.py)',
    what: 'Tracks the cumulative portfolio return over the last year. Max drawdown = largest peak-to-trough decline. Current drawdown = how far below the most recent peak.',
    meaning: 'The worst losing streak over the past year. "Max drawdown -14%" means at its worst, the portfolio had lost 14% from a previous high before recovering. Current drawdown tells you if you\'re in a drawdown right now.',
    limitation: 'Only covers the last ~1 year. Longer historical drawdowns are not captured.',
  },
  diversification: {
    label: 'Diversification Ratio',
    source: 'Portfolio_Risk_Metrics (DC_Portfolio_Risk_Metrics.py)',
    what: 'Weighted average of individual stock annualized volatilities ÷ actual portfolio annualized volatility. Ratio > 1 means your portfolio is less volatile than its individual holdings.',
    meaning: 'Measures how much your positions "cancel out" each other\'s swings. Ratio 1.0 = no diversification benefit (all stocks move together). Ratio 1.8 = your portfolio is 44% less volatile than the average individual stock. Higher = better diversified.',
    limitation: 'Only covers tickers with yfinance price data.',
  },
};

// ─── Portfolio risk computation ─────────────────────────────────

function computePortfolioRisk(tpData, yfData, data) {
  if (!data?.length) return null;

  const totalValue = data.reduce((s, d) => s + (parseFloat(d.Value_eur) || 0), 0);
  const useWeights = totalValue > 0;

  const dedupedByTicker = new Map();
  for (const d of data.filter(d => d.Ticker)) {
    const ticker = d.Ticker.toUpperCase();
    const value = parseFloat(d.Value_eur) || 0;
    if (dedupedByTicker.has(ticker)) {
      const existing = dedupedByTicker.get(ticker);
      existing.value += value;
    } else {
      dedupedByTicker.set(ticker, { ticker, company: d.Company || '', value });
    }
  }

  const positions = [...dedupedByTicker.values()]
    .map(({ ticker, company, value }) => {
      const tp = tpData[ticker] || {};
      const yf = yfData[ticker] || {};
      const weight = useWeights ? (totalValue > 0 ? value / totalValue : 0) : (1 / dedupedByTicker.size);

      const beta = parseFloat(yf.beta);
      const sharpe = parseFloat(tp.sharpe_ratio);
      const returnProb = parseFloat(tp.return_probability);
      const expectedReturn = parseFloat(tp.expected_q_return);
      const residualVol = parseFloat(tp.residual_volatility);
      const riskAdjReturn = parseFloat(tp.risk_adj_q_return);
      const perf6m = parseFloat(tp.six_month_performance);
      const perfYtd = parseFloat(tp.YTD);
      const regime = tp.return_probability_regime || null;

      return {
        ticker,
        company,
        value,
        weight,
        beta: isNaN(beta) ? null : beta,
        sharpe: isNaN(sharpe) ? null : sharpe,
        returnProb: isNaN(returnProb) ? null : returnProb,
        expectedReturn: isNaN(expectedReturn) ? null : expectedReturn,
        residualVol: isNaN(residualVol) ? null : residualVol,
        riskAdjReturn: isNaN(riskAdjReturn) ? null : riskAdjReturn,
        perf6m: isNaN(perf6m) ? null : perf6m,
        perfYtd: isNaN(perfYtd) ? null : perfYtd,
        regime,
        hasData: !isNaN(beta) || !isNaN(sharpe) || !isNaN(returnProb),
      };
    })
    .filter(p => p.weight > 0);

  if (positions.length === 0) return null;

  const weightedAvg = (items, field) => {
    const valid = items.filter(p => p[field] != null);
    const totalW = valid.reduce((s, p) => s + p.weight, 0);
    if (totalW <= 0) return { value: null, count: valid.length, total: items.length, coveragePct: 0 };
    const avg = valid.reduce((s, p) => s + p.weight * p[field], 0) / totalW;
    return { value: avg, count: valid.length, total: items.length, coveragePct: totalW * 100 };
  };

  const beta = weightedAvg(positions, 'beta');
  const sharpe = weightedAvg(positions, 'sharpe');
  const returnProb = weightedAvg(positions, 'returnProb');
  const expectedReturn = weightedAvg(positions, 'expectedReturn');
  const riskAdjReturn = weightedAvg(positions, 'riskAdjReturn');

  const worstPerf = positions
    .filter(p => p.perf6m != null || p.perfYtd != null)
    .map(p => ({
      ticker: p.ticker,
      worst: Math.min(p.perf6m ?? 0, p.perfYtd ?? 0),
      weight: p.weight,
      perf6m: p.perf6m,
      perfYtd: p.perfYtd,
    }))
    .sort((a, b) => a.worst - b.worst);

  const maxDrawdownContributor = worstPerf.length > 0 ? worstPerf[0] : null;

  const regimes = positions.filter(p => p.regime).map(p => p.regime);
  const dominantRegime = regimes.length > 0
    ? regimes.sort((a, b) => regimes.filter(r => r === b).length - regimes.filter(r => r === a).length)[0]
    : null;

  return {
    beta,
    sharpe,
    returnProb,
    expectedReturn,
    riskAdjReturn,
    maxDrawdownContributor,
    positions,
    totalValue,
    useWeights,
    dominantRegime,
  };
}

// ─── Exposure risk computation (DAS-2451) ──────────────────────

function computeExposureRisk(exposures, tpData, yfData) {
  if (!exposures?.length) return null;

  const totalExposure = exposures.reduce((s, e) => s + (parseFloat(e.total_amount_exposure) || 0), 0);
  const useWeights = totalExposure > 0;

  const positions = exposures
    .filter(e => e.ticker)
    .map(e => {
      const ticker = (e.ticker || '').toUpperCase();
      const tp = tpData[ticker] || {};
      const yf = yfData[ticker] || {};
      const totalExp = parseFloat(e.total_amount_exposure) || 0;
      const stockExp = parseFloat(e.stock_exposure) || 0;
      const trackerExp = parseFloat(e.tracker_exposure_amount) || 0;
      const weight = useWeights
        ? (totalExposure > 0 ? totalExp / totalExposure : 0)
        : (1 / exposures.filter(x => x.ticker).length);

      const beta = parseFloat(yf.beta);
      const sharpe = parseFloat(tp.sharpe_ratio);
      const returnProb = parseFloat(tp.return_probability);
      const expectedReturn = parseFloat(tp.expected_q_return);
      const residualVol = parseFloat(tp.residual_volatility);
      const riskAdjReturn = parseFloat(tp.risk_adj_q_return);
      const regime = tp.return_probability_regime || null;

      return {
        ticker,
        company: e.company || '',
        totalExposure: totalExp,
        stockExposure: stockExp,
        trackerExposure: trackerExp,
        trackerDetails: e.tracker_exposure || null,
        weight,
        beta: isNaN(beta) ? null : beta,
        sharpe: isNaN(sharpe) ? null : sharpe,
        returnProb: isNaN(returnProb) ? null : returnProb,
        expectedReturn: isNaN(expectedReturn) ? null : expectedReturn,
        residualVol: isNaN(residualVol) ? null : residualVol,
        riskAdjReturn: isNaN(riskAdjReturn) ? null : riskAdjReturn,
        regime,
        hasData: !isNaN(beta) || !isNaN(sharpe) || !isNaN(returnProb),
        holdingType: stockExp > 0 && trackerExp > 0 ? 'both' : stockExp > 0 ? 'direct' : trackerExp > 0 ? 'etf' : 'unknown',
      };
    })
    .filter(p => p.weight > 0);

  if (positions.length === 0) return null;

  const weightedAvg = (items, field) => {
    const valid = items.filter(p => p[field] != null);
    const totalW = valid.reduce((s, p) => s + p.weight, 0);
    if (totalW <= 0) return { value: null, count: valid.length, total: items.length, coveragePct: 0 };
    const avg = valid.reduce((s, p) => s + p.weight * p[field], 0) / totalW;
    return { value: avg, count: valid.length, total: items.length, coveragePct: totalW * 100 };
  };

  return {
    beta: weightedAvg(positions, 'beta'),
    sharpe: weightedAvg(positions, 'sharpe'),
    returnProb: weightedAvg(positions, 'returnProb'),
    expectedReturn: weightedAvg(positions, 'expectedReturn'),
    positions,
    totalExposure,
    useWeights,
    directOnlyCount: positions.filter(p => p.holdingType === 'direct').length,
    etfOnlyCount: positions.filter(p => p.holdingType === 'etf').length,
    bothCount: positions.filter(p => p.holdingType === 'both').length,
  };
}

const HOLDING_TYPE_CHIPS = {
  both: { label: 'Both', color: '#1565c0', bg: '#e3f2fd' },
  direct: { label: 'Direct', color: '#2e7d32', bg: '#e8f5e9' },
  etf: { label: 'via ETF', color: '#7b1fa2', bg: '#f3e5f5' },
  unknown: { label: '?', color: '#757575', bg: '#f5f5f5' },
};

// ─── Data source tooltip component ──────────────────────────────

const DataSourceChip = ({ sourceKey }) => {
  const info = DATA_SOURCES[sourceKey];
  if (!info) return null;
  return (
    <Tooltip
      arrow
      placement="bottom-start"
      title={
        <Box sx={{ maxWidth: 360, p: 0.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>{info.label}</Typography>
          <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
            <strong>Data source:</strong> {info.source}
          </Typography>
          <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
            <strong>How it's calculated:</strong> {info.what}
          </Typography>
          <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
            <strong>What it means:</strong> {info.meaning}
          </Typography>
          <Typography variant="caption" display="block" sx={{ color: '#ffab91' }}>
            <strong>Limitation:</strong> {info.limitation}
          </Typography>
          {info.portfolio && (
            <Typography variant="caption" display="block" sx={{ mt: 0.5, color: '#90caf9' }}>
              <strong>Portfolio level:</strong> {info.portfolio}
            </Typography>
          )}
        </Box>
      }
    >
      <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled', cursor: 'help', ml: 0.5, verticalAlign: 'middle' }} />
    </Tooltip>
  );
};

// ─── Summary metric card ────────────────────────────────────────

function coverageLabel(count, total, coveragePct) {
  if (count === total) return `All ${total} positions`;
  return `${count} of ${total} positions (${Math.round(coveragePct)}% of value)`;
}

const MetricCard = ({ title, value, subtitle, color, sourceKey, coverage, coveredTickers, missingTickers }) => {
  const [showTickers, setShowTickers] = useState(false);
  const isLow = coverage && coverage.coveragePct < 50;
  const text = coverage ? coverageLabel(coverage.count, coverage.total, coverage.coveragePct) : null;
  const hasTickers = coveredTickers?.length > 0 || missingTickers?.length > 0;

  return (
    <Paper variant="outlined" sx={{ p: 2, flex: '1 1 160px', minWidth: 160 }}>
      <Box display="flex" alignItems="center">
        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.6rem' }}>
          {title}
        </Typography>
        {sourceKey && <DataSourceChip sourceKey={sourceKey} />}
      </Box>
      <Typography variant="h5" sx={{ fontWeight: 700, color: color || 'text.primary', mt: 0.5 }}>
        {value}
      </Typography>
      {subtitle && (
        <Typography variant="body2" sx={{ fontWeight: 600, color: color || 'text.secondary', mt: 0.25 }}>
          {subtitle}
        </Typography>
      )}
      {text && (
        <Typography
          variant="caption"
          sx={{
            display: 'block', mt: 0.5,
            color: isLow ? '#e65100' : 'text.disabled',
            cursor: hasTickers ? 'pointer' : 'default',
            '&:hover': hasTickers ? { textDecoration: 'underline' } : {},
          }}
          onClick={hasTickers ? () => setShowTickers(prev => !prev) : undefined}
        >
          {text}{hasTickers ? (showTickers ? ' ▴' : ' ▾') : ''}
        </Typography>
      )}
      <Collapse in={showTickers}>
        <Box sx={{ mt: 1 }}>
          {coveredTickers?.length > 0 && (
            <Box sx={{ mb: 0.75 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#2e7d32', fontSize: '0.6rem', display: 'block', mb: 0.3 }}>
                Included ({coveredTickers.length})
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={0.3}>
                {coveredTickers.map(t => (
                  <Chip key={t} label={t} size="small" variant="outlined"
                    sx={{ fontSize: '0.6rem', height: 20, borderColor: '#a5d6a7' }} />
                ))}
              </Box>
            </Box>
          )}
          {missingTickers?.length > 0 && (
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#c62828', fontSize: '0.6rem', display: 'block', mb: 0.3 }}>
                Not included ({missingTickers.length})
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={0.3}>
                {missingTickers.map(t => (
                  <Chip key={t} label={t} size="small" variant="outlined"
                    sx={{ fontSize: '0.6rem', height: 20, borderColor: '#ef9a9a' }} />
                ))}
              </Box>
            </Box>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
};

// ─── Main modal component ───────────────────────────────────────

const fmtCurrency = (v) => {
  if (v == null || isNaN(v)) return '—';
  return `€${Number(v).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

// DAS-2982: one plain-language sentence built from the numbers already computed
// in Portfolio_Risk_Metrics — no new data. Band = annualised volatility (the
// ~68% / "two years out of three" range for a normal spread); the concrete shock
// is the observed worst peak-to-trough drop of the past year. Honest about
// coverage: if most value has no daily price, the sentence says it describes
// only the priced part.
function plainRiskSentence(pr) {
  if (!pr || pr.annualized_volatility == null) return null;
  const band = Math.round(pr.annualized_volatility);
  let s = `In a normal year, this portfolio swings about ±${band}%. Two years out of three it stays within that range; the rest of the time it moves more.`;
  if (pr.max_drawdown_pct != null) {
    s += ` Over the past year, its deepest drop from a high was ${Math.abs(pr.max_drawdown_pct).toFixed(0)}%.`;
  }
  return s;
}

const PAModal_Risk = ({ tpData, yfData, data, source = 'portfolio', portfolioRisk = null, portfolioRiskExposure = null, exposures = [] }) => {
  const [search, setSearch] = useState('');
  const [orderBy, setOrderBy] = useState('returnProb');
  const [order, setOrder] = useState('desc');
  const [methodOpen, setMethodOpen] = useState(false);
  const [expSearch, setExpSearch] = useState('');
  const [expOrderBy, setExpOrderBy] = useState('totalExposure');
  const [expOrder, setExpOrder] = useState('desc');
  const [expSectionOpen, setExpSectionOpen] = useState(true);

  const risk = useMemo(() => computePortfolioRisk(tpData, yfData, data), [tpData, yfData, data]);
  const exposureRisk = useMemo(() => computeExposureRisk(exposures, tpData, yfData), [exposures, tpData, yfData]);

  const filteredPositions = useMemo(() => {
    if (!risk?.positions) return [];
    let list = risk.positions.filter(p => p.hasData);
    if (search) {
      const lower = search.toLowerCase();
      list = list.filter(r =>
        r.ticker.toLowerCase().includes(lower) ||
        r.company.toLowerCase().includes(lower)
      );
    }
    return [...list].sort((a, b) => {
      const av = a[orderBy] ?? -Infinity;
      const bv = b[orderBy] ?? -Infinity;
      return order === 'asc' ? av - bv : bv - av;
    });
  }, [risk, search, orderBy, order]);

  const handleSort = (field) => {
    if (orderBy === field) {
      setOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setOrderBy(field);
      setOrder('desc');
    }
  };

  const handleExpSort = (field) => {
    if (expOrderBy === field) {
      setExpOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setExpOrderBy(field);
      setExpOrder('desc');
    }
  };

  const filteredExposures = useMemo(() => {
    if (!exposureRisk?.positions) return [];
    let list = exposureRisk.positions;
    if (expSearch) {
      const lower = expSearch.toLowerCase();
      list = list.filter(r =>
        r.ticker.toLowerCase().includes(lower) ||
        r.company.toLowerCase().includes(lower)
      );
    }
    return [...list].sort((a, b) => {
      const av = a[expOrderBy] ?? -Infinity;
      const bv = b[expOrderBy] ?? -Infinity;
      return expOrder === 'asc' ? av - bv : bv - av;
    });
  }, [exposureRisk, expSearch, expOrderBy, expOrder]);

  const prTickers = useMemo(() => {
    if (!portfolioRisk) return null;
    const split = (s) => s ? s.split(',').map(t => t.trim()).filter(Boolean).sort() : [];
    const covered = split(portfolioRisk.tickers_covered);
    const missing = split(portfolioRisk.tickers_not_covered);
    if (covered.length > 0 || missing.length > 0) return { covered, missing };
    if (!data?.length) return null;
    const yfKeys = new Set(Object.keys(yfData || {}).map(k => k.toUpperCase()));
    const unique = [...new Set(data.filter(d => d.Ticker).map(d => d.Ticker.toUpperCase()))];
    return {
      covered: unique.filter(t => yfKeys.has(t)).sort(),
      missing: unique.filter(t => !yfKeys.has(t)).sort(),
    };
  }, [portfolioRisk, data, yfData]);

  const ptCov = useMemo(() => {
    if (!risk?.positions) return {};
    const pos = risk.positions;
    const make = (field) => ({
      covered: pos.filter(p => p[field] != null).map(p => p.ticker).sort(),
      missing: pos.filter(p => p[field] == null).map(p => p.ticker).sort(),
    });
    return {
      beta: make('beta'),
      sharpe: make('sharpe'),
      returnProb: make('returnProb'),
      expectedReturn: make('expectedReturn'),
    };
  }, [risk]);

  if (!risk && !portfolioRisk && !exposureRisk) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" sx={{ minHeight: 200 }}>
        <Typography color="text.secondary">No risk data available — requires ticker performance data</Typography>
      </Box>
    );
  }

  const betaLevel = risk?.beta?.value != null ? getBetaLevel(risk.beta.value) : null;
  const sharpeLevel = risk?.sharpe?.value != null ? getSharpeLevel(risk.sharpe.value) : null;
  const probLevel = risk?.returnProb?.value != null ? getProbLevel(risk.returnProb.value) : null;

  const pr = portfolioRisk;
  const prCoverage = pr ? { count: pr.tickers_with_data, total: pr.tickers_total, coveragePct: pr.price_coverage_pct } : null;

  const volLevel = pr?.annualized_volatility != null
    ? (pr.annualized_volatility > 25 ? { label: 'High', color: '#c62828' }
      : pr.annualized_volatility > 15 ? { label: 'Moderate', color: '#f57c00' }
      : { label: 'Low', color: '#2e7d32' })
    : null;

  const pSharpeLevel = pr?.portfolio_sharpe != null ? getSharpeLevel(pr.portfolio_sharpe) : null;

  const divLabel = pr?.diversification_ratio != null
    ? (pr.diversification_ratio >= 1.5 ? 'Well diversified' : pr.diversification_ratio >= 1.2 ? 'Moderate' : 'Low diversification')
    : null;
  const divColor = pr?.diversification_ratio != null
    ? (pr.diversification_ratio >= 1.5 ? '#2e7d32' : pr.diversification_ratio >= 1.2 ? '#f57c00' : '#c62828')
    : undefined;

  return (
    <Box>
      {/* Methodology section */}
      <Paper
        variant="outlined"
        sx={{ p: 1.5, mb: 2, bgcolor: '#f5f5f5', cursor: 'pointer' }}
        onClick={() => setMethodOpen(prev => !prev)}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <InfoOutlinedIcon fontSize="small" color="action" />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              How to read this — click each (i) icon for data source and method
            </Typography>
          </Box>
          <IconButton size="small">
            {methodOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
        <Collapse in={methodOpen}>
          <Box mt={1.5}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {pr ? (
                <>
                  <strong>Portfolio-level metrics</strong> (Volatility, VaR, Sharpe, Drawdown, Diversification)
                  are pre-computed daily from actual weighted portfolio returns using 1 year of daily close prices.
                  These are real portfolio metrics — not averages of individual stock metrics.
                  {pr.price_coverage_pct < 50 && (
                    <> Only {fmtPct(pr.price_coverage_pct, 0)} of portfolio value had yfinance price data — the rest are private funds, deposits, or unlisted assets.</>
                  )}
                </>
              ) : (
                <>
                  Portfolio-level values are value-weighted averages of per-ticker metrics.
                  True portfolio volatility and VaR require historical price data which is being loaded.
                </>
              )}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              <strong>Per-ticker metrics</strong> (Beta, Sharpe, P(Return+)) come from Ticker_Performance_Data and YF_Data.
              Hover the (i) icon next to each metric to see exactly what data it uses and how it's calculated.
            </Typography>
            {risk?.dominantRegime && (
              <Typography variant="body2" color="text.secondary">
                <strong>Market regime:</strong> Currently detected as <Chip label={risk.dominantRegime} size="small" variant="outlined" sx={{ mx: 0.5 }} />.
              </Typography>
            )}
          </Box>
        </Collapse>
      </Paper>

      {/* Pre-computed portfolio metrics */}
      {pr && (
        <>
          <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 1 }}>
            Portfolio-Level Metrics (from actual daily returns)
          </Typography>
          {/* DAS-2982: plain-language reading of the numbers below */}
          {plainRiskSentence(pr) && (
            <Paper
              variant="outlined"
              data-testid="plain-risk-sentence"
              sx={{ p: 1.5, mb: 2, bgcolor: '#e8f4fd', borderColor: '#90caf9' }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#0d3c61' }}>
                {plainRiskSentence(pr)}
              </Typography>
              {pr.price_coverage_pct != null && pr.price_coverage_pct < 95 && (
                <Typography variant="caption" sx={{ display: 'block', mt: 0.75, color: '#e65100' }}>
                  These figures cover only the {fmtPct(pr.price_coverage_pct, 0)} of your portfolio value that trades with daily market prices — cash, deposits and private funds are not included and are generally steadier.
                </Typography>
              )}
            </Paper>
          )}
          <Box display="flex" gap={2} mb={2} flexWrap="wrap">
            <MetricCard
              title="Portfolio Volatility"
              value={pr.annualized_volatility != null ? fmtPct(pr.annualized_volatility, 1) : '—'}
              subtitle={volLevel?.label}
              color={volLevel?.color}
              sourceKey="portfolioVol"
              coverage={prCoverage}
              coveredTickers={prTickers?.covered}
              missingTickers={prTickers?.missing}
            />
            <MetricCard
              title="Portfolio Sharpe"
              value={pr.portfolio_sharpe != null ? fmtNum(pr.portfolio_sharpe) : '—'}
              subtitle={pSharpeLevel?.label}
              color={pSharpeLevel?.color}
              sourceKey="portfolioSharpe"
              coverage={prCoverage}
              coveredTickers={prTickers?.covered}
              missingTickers={prTickers?.missing}
            />
            <MetricCard
              title="VaR 95% (1-day)"
              value={pr.var_95_1d_eur != null ? fmtCurrency(pr.var_95_1d_eur) : '—'}
              subtitle={pr.var_95_1d_pct != null ? `${fmtPct(pr.var_95_1d_pct)} of covered value` : null}
              color="#e65100"
              sourceKey="portfolioVar"
              coverage={prCoverage}
              coveredTickers={prTickers?.covered}
              missingTickers={prTickers?.missing}
            />
            <MetricCard
              title="VaR 99% (1-day)"
              value={pr.var_99_1d_eur != null ? fmtCurrency(pr.var_99_1d_eur) : '—'}
              subtitle={pr.var_95_5d_eur != null ? `5-day 95%: ${fmtCurrency(pr.var_95_5d_eur)}` : null}
              color="#c62828"
              sourceKey="portfolioVar"
              coverage={prCoverage}
              coveredTickers={prTickers?.covered}
              missingTickers={prTickers?.missing}
            />
            <MetricCard
              title="Max Drawdown"
              value={pr.max_drawdown_pct != null ? fmtPct(pr.max_drawdown_pct, 1) : '—'}
              subtitle={pr.current_drawdown_pct != null && pr.current_drawdown_pct < -0.5 ? `Current: ${fmtPct(pr.current_drawdown_pct, 1)}` : pr.current_drawdown_pct != null ? 'At/near peak' : null}
              color={pr.max_drawdown_pct != null ? (pr.max_drawdown_pct < -20 ? '#c62828' : pr.max_drawdown_pct < -10 ? '#f57c00' : '#2e7d32') : undefined}
              sourceKey="maxDrawdown"
              coverage={prCoverage}
              coveredTickers={prTickers?.covered}
              missingTickers={prTickers?.missing}
            />
            <MetricCard
              title="Diversification Ratio"
              value={pr.diversification_ratio != null ? `${fmtNum(pr.diversification_ratio)}×` : '—'}
              subtitle={divLabel}
              color={divColor}
              sourceKey="diversification"
              coverage={prCoverage}
              coveredTickers={prTickers?.covered}
              missingTickers={prTickers?.missing}
            />
          </Box>
          {pr.computed_at && (
            <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mb: 2 }}>
              Last computed: {new Date(pr.computed_at).toLocaleString()} · {pr.data_points_used} trading days of data
            </Typography>
          )}
        </>
      )}

      {/* Per-ticker weighted averages */}
      {risk && (
        <>
          <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 1 }}>
            Per-Ticker Averages (weighted)
          </Typography>
          <Box display="flex" gap={2} mb={3} flexWrap="wrap">
            <MetricCard
              title="Avg Beta"
              value={risk.beta.value != null ? fmtNum(risk.beta.value) : '—'}
              subtitle={betaLevel?.label}
              color={betaLevel?.color}
              sourceKey="beta"
              coverage={risk.beta}
              coveredTickers={ptCov.beta?.covered}
              missingTickers={ptCov.beta?.missing}
            />
            <MetricCard
              title="Avg Sharpe (per-ticker)"
              value={risk.sharpe.value != null ? fmtNum(risk.sharpe.value) : '—'}
              subtitle={sharpeLevel?.label}
              color={sharpeLevel?.color}
              sourceKey="sharpe"
              coverage={risk.sharpe}
              coveredTickers={ptCov.sharpe?.covered}
              missingTickers={ptCov.sharpe?.missing}
            />
            <MetricCard
              title="Avg P(Return+)"
              value={risk.returnProb.value != null ? fmtPct(risk.returnProb.value, 0) : '—'}
              subtitle={probLevel?.label}
              color={probLevel?.color}
              sourceKey="returnProb"
              coverage={risk.returnProb}
              coveredTickers={ptCov.returnProb?.covered}
              missingTickers={ptCov.returnProb?.missing}
            />
            <MetricCard
              title="Avg Exp. Q Return"
              value={risk.expectedReturn.value != null ? `${risk.expectedReturn.value > 0 ? '+' : ''}${fmtPct(risk.expectedReturn.value)}` : '—'}
              subtitle={risk.expectedReturn.value != null
                ? (risk.expectedReturn.value > 3 ? 'Strong' : risk.expectedReturn.value > 0 ? 'Positive' : 'Negative')
                : null}
              color={risk.expectedReturn.value != null
                ? (risk.expectedReturn.value > 3 ? '#1b5e20' : risk.expectedReturn.value > 0 ? '#2e7d32' : '#c62828')
                : undefined}
              sourceKey="expectedReturn"
              coverage={risk.expectedReturn}
              coveredTickers={ptCov.expectedReturn?.covered}
              missingTickers={ptCov.expectedReturn?.missing}
            />
          </Box>
        </>
      )}

      {/* Worst contributor */}
      {risk?.maxDrawdownContributor && (
        <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: '#fff8e1' }}>
          <Box display="flex" alignItems="center" gap={0.5}>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.6rem' }}>
              Biggest Recent Loss
            </Typography>
            <DataSourceChip sourceKey="drawdown" />
          </Box>
          <Box display="flex" alignItems="baseline" gap={1} mt={0.5} flexWrap="wrap">
            <Typography variant="body1" sx={{ fontWeight: 700, color: '#c62828' }}>
              {risk.maxDrawdownContributor.ticker}
            </Typography>
            <Typography variant="body2" sx={{ color: '#c62828' }}>
              {fmtPct(risk.maxDrawdownContributor.worst, 1)} (worst of 6M / YTD)
            </Typography>
            <Typography variant="body2" color="text.secondary">
              · {fmtPct(risk.maxDrawdownContributor.weight * 100, 1)} portfolio weight
            </Typography>
            {risk.maxDrawdownContributor.perf6m != null && risk.maxDrawdownContributor.perfYtd != null && (
              <Typography variant="caption" color="text.disabled">
                (6M: {fmtPct(risk.maxDrawdownContributor.perf6m, 1)} · YTD: {fmtPct(risk.maxDrawdownContributor.perfYtd, 1)})
              </Typography>
            )}
          </Box>
        </Paper>
      )}

      {/* Per-ticker table */}
      {risk && <><Box display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
        <TextField
          size="small"
          placeholder="Search ticker or company..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ width: 280 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <Typography variant="body2" color="text.secondary">
          {filteredPositions.length} of {risk.positions.length} positions with risk data
        </Typography>
      </Box>

      <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 1 }}>
        Per-Ticker Risk Profile
      </Typography>

      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: '45vh' }}>
        <MuiTable size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Ticker</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>
                <TableSortLabel active={orderBy === 'weight'} direction={orderBy === 'weight' ? order : 'desc'} onClick={() => handleSort('weight')}>
                  Weight
                </TableSortLabel>
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>
                <Box display="inline-flex" alignItems="center">
                  <TableSortLabel active={orderBy === 'beta'} direction={orderBy === 'beta' ? order : 'desc'} onClick={() => handleSort('beta')}>
                    Beta
                  </TableSortLabel>
                  <DataSourceChip sourceKey="beta" />
                </Box>
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>
                <Box display="inline-flex" alignItems="center">
                  <TableSortLabel active={orderBy === 'sharpe'} direction={orderBy === 'sharpe' ? order : 'desc'} onClick={() => handleSort('sharpe')}>
                    Sharpe
                  </TableSortLabel>
                  <DataSourceChip sourceKey="sharpe" />
                </Box>
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>
                <Box display="inline-flex" alignItems="center">
                  <TableSortLabel active={orderBy === 'returnProb'} direction={orderBy === 'returnProb' ? order : 'desc'} onClick={() => handleSort('returnProb')}>
                    P(Return+)
                  </TableSortLabel>
                  <DataSourceChip sourceKey="returnProb" />
                </Box>
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>
                <Box display="inline-flex" alignItems="center">
                  <TableSortLabel active={orderBy === 'expectedReturn'} direction={orderBy === 'expectedReturn' ? order : 'desc'} onClick={() => handleSort('expectedReturn')}>
                    Exp. Q Ret.
                  </TableSortLabel>
                  <DataSourceChip sourceKey="expectedReturn" />
                </Box>
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>
                <Box display="inline-flex" alignItems="center">
                  <TableSortLabel active={orderBy === 'residualVol'} direction={orderBy === 'residualVol' ? order : 'desc'} onClick={() => handleSort('residualVol')}>
                    Resid. Vol
                  </TableSortLabel>
                  <DataSourceChip sourceKey="residualVol" />
                </Box>
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>
                <Box display="inline-flex" alignItems="center">
                  <TableSortLabel active={orderBy === 'riskAdjReturn'} direction={orderBy === 'riskAdjReturn' ? order : 'desc'} onClick={() => handleSort('riskAdjReturn')}>
                    Risk-Adj Q
                  </TableSortLabel>
                  <DataSourceChip sourceKey="riskAdjReturn" />
                </Box>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredPositions.map((row, idx) => {
              const betaLvl = row.beta != null ? getBetaLevel(row.beta) : null;
              const sharpeLvl = row.sharpe != null ? getSharpeLevel(row.sharpe) : null;
              const probLvl = row.returnProb != null ? getProbLevel(row.returnProb) : null;
              const dash = <Typography variant="caption" color="text.disabled">—</Typography>;

              return (
                <TableRow key={row.ticker || idx} hover>
                  <TableCell>
                    <ClickableTickerChip ticker={row.ticker} source={source} />
                    {row.company && (
                      <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.25 }}>
                        {row.company}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">{fmtPct(row.weight * 100, 1)}</TableCell>
                  <TableCell align="right">
                    {row.beta != null ? (
                      <Typography variant="body2" sx={{ color: betaLvl?.color || 'text.primary', fontWeight: 500 }}>
                        {fmtNum(row.beta)}
                      </Typography>
                    ) : dash}
                  </TableCell>
                  <TableCell align="right">
                    {row.sharpe != null ? (
                      <Typography variant="body2" sx={{ color: sharpeLvl?.color || 'text.primary', fontWeight: 500 }}>
                        {fmtNum(row.sharpe)}
                      </Typography>
                    ) : dash}
                  </TableCell>
                  <TableCell align="right">
                    {row.returnProb != null ? (
                      <Tooltip title={`Regime: ${row.regime || '?'} · Exp Q: ${row.expectedReturn != null ? fmtPct(row.expectedReturn) : '?'}`} arrow>
                        <Typography variant="body2" sx={{ color: probLvl?.color || 'text.primary', fontWeight: 500 }}>
                          {fmtPct(row.returnProb, 0)}
                        </Typography>
                      </Tooltip>
                    ) : dash}
                  </TableCell>
                  <TableCell align="right">
                    {row.expectedReturn != null ? (
                      <Typography variant="body2" sx={{
                        color: row.expectedReturn > 3 ? '#1b5e20' : row.expectedReturn > 0 ? '#2e7d32' : '#c62828',
                        fontWeight: 500,
                      }}>
                        {row.expectedReturn > 0 ? '+' : ''}{fmtPct(row.expectedReturn)}
                      </Typography>
                    ) : dash}
                  </TableCell>
                  <TableCell align="right">
                    {row.residualVol != null ? (
                      <Typography variant="body2" sx={{
                        color: row.residualVol > 15 ? '#c62828' : row.residualVol > 10 ? '#f57c00' : '#2e7d32',
                        fontWeight: 500,
                      }}>
                        {fmtPct(row.residualVol, 1)}
                      </Typography>
                    ) : dash}
                  </TableCell>
                  <TableCell align="right">
                    {row.riskAdjReturn != null ? (
                      <Typography variant="body2" sx={{
                        color: row.riskAdjReturn > 0.3 ? '#1b5e20' : row.riskAdjReturn > 0 ? '#2e7d32' : '#c62828',
                        fontWeight: 500,
                      }}>
                        {fmtNum(row.riskAdjReturn, 3)}
                      </Typography>
                    ) : dash}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </MuiTable>
      </TableContainer></>}

      {!portfolioRisk && (
        <Alert severity="info" variant="outlined" sx={{ mt: 2 }}>
          <AlertTitle sx={{ fontSize: '0.8rem' }}>Portfolio-level metrics not available</AlertTitle>
          <Typography variant="caption">
            {source === 'portfolio'
              ? 'Pre-computed portfolio metrics (Volatility, VaR, Sharpe, Drawdown, Diversification) are computed daily by a batch job. If you\'re seeing this, the batch job hasn\'t run yet for this portfolio.'
              : 'Portfolio-level metrics could not be computed — not enough tickers with historical price data from yfinance.'}
            {' '}Per-ticker metrics are shown above from existing data.
          </Typography>
        </Alert>
      )}

      {/* ─── DAS-2451: Exposure Risk Profile ──────────────────────── */}
      {exposureRisk && exposureRisk.positions.length > 0 && (
        <>
          <Divider sx={{ my: 3 }} />

          <Paper
            variant="outlined"
            sx={{ p: 1.5, mb: 2, bgcolor: '#f3e5f5', cursor: 'pointer' }}
            onClick={() => setExpSectionOpen(prev => !prev)}
          >
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Exposure Risk Profile
                </Typography>
                <Chip
                  label={`${exposureRisk.positions.length} tickers`}
                  size="small" variant="outlined"
                  sx={{ fontSize: '0.6rem', height: 20 }}
                />
                {exposureRisk.bothCount > 0 && (
                  <Chip label={`${exposureRisk.bothCount} held both ways`} size="small"
                    sx={{ fontSize: '0.6rem', height: 20, bgcolor: '#e3f2fd', color: '#1565c0' }} />
                )}
                {exposureRisk.etfOnlyCount > 0 && (
                  <Chip label={`${exposureRisk.etfOnlyCount} via ETF only`} size="small"
                    sx={{ fontSize: '0.6rem', height: 20, bgcolor: '#f3e5f5', color: '#7b1fa2' }} />
                )}
              </Box>
              <IconButton size="small">
                {expSectionOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
          </Paper>

          <Collapse in={expSectionOpen}>
            <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
              <Typography variant="caption">
                This shows risk for your <strong>total economic exposure</strong> — including stocks held indirectly
                through ETFs and Funds. Each ticker shows: (a) total exposure value, (b) direct holdings,
                (c) via ETF/Fund holdings. Risk metrics (Beta, Sharpe, etc.) are per-ticker and identical
                regardless of how you hold the stock — what changes is the <em>weight</em> and <em>risk contribution</em>.
              </Typography>
            </Alert>

            {/* Exposure-weighted summary cards */}
            <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 1 }}>
              Exposure-Weighted Averages
            </Typography>
            <Box display="flex" gap={2} mb={3} flexWrap="wrap">
              <MetricCard
                title="Avg Beta (exposure)"
                value={exposureRisk.beta.value != null ? fmtNum(exposureRisk.beta.value) : '—'}
                subtitle={exposureRisk.beta.value != null ? getBetaLevel(exposureRisk.beta.value).label : null}
                color={exposureRisk.beta.value != null ? getBetaLevel(exposureRisk.beta.value).color : undefined}
                sourceKey="beta"
                coverage={exposureRisk.beta}
              />
              <MetricCard
                title="Avg Sharpe (exposure)"
                value={exposureRisk.sharpe.value != null ? fmtNum(exposureRisk.sharpe.value) : '—'}
                subtitle={exposureRisk.sharpe.value != null ? getSharpeLevel(exposureRisk.sharpe.value).label : null}
                color={exposureRisk.sharpe.value != null ? getSharpeLevel(exposureRisk.sharpe.value).color : undefined}
                sourceKey="sharpe"
                coverage={exposureRisk.sharpe}
              />
              <MetricCard
                title="Avg P(Return+) (exposure)"
                value={exposureRisk.returnProb.value != null ? fmtPct(exposureRisk.returnProb.value, 0) : '—'}
                subtitle={exposureRisk.returnProb.value != null ? getProbLevel(exposureRisk.returnProb.value).label : null}
                color={exposureRisk.returnProb.value != null ? getProbLevel(exposureRisk.returnProb.value).color : undefined}
                sourceKey="returnProb"
                coverage={exposureRisk.returnProb}
              />
              <MetricCard
                title="Avg Exp. Q Return (exposure)"
                value={exposureRisk.expectedReturn.value != null ? `${exposureRisk.expectedReturn.value > 0 ? '+' : ''}${fmtPct(exposureRisk.expectedReturn.value)}` : '—'}
                subtitle={exposureRisk.expectedReturn.value != null
                  ? (exposureRisk.expectedReturn.value > 3 ? 'Strong' : exposureRisk.expectedReturn.value > 0 ? 'Positive' : 'Negative')
                  : null}
                color={exposureRisk.expectedReturn.value != null
                  ? (exposureRisk.expectedReturn.value > 3 ? '#1b5e20' : exposureRisk.expectedReturn.value > 0 ? '#2e7d32' : '#c62828')
                  : undefined}
                sourceKey="expectedReturn"
                coverage={exposureRisk.expectedReturn}
              />
            </Box>

            {/* Portfolio-level exposure metrics (VaR, vol, Sharpe, drawdown) */}
            {portfolioRiskExposure && (
              <>
                <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 1 }}>
                  Portfolio-Level Metrics (full exposure)
                </Typography>
                <Box display="flex" gap={2} mb={2} flexWrap="wrap">
                  <MetricCard
                    title="Exposure Volatility"
                    value={portfolioRiskExposure.annualized_volatility != null ? fmtPct(portfolioRiskExposure.annualized_volatility, 1) : '—'}
                    subtitle={portfolioRiskExposure.annualized_volatility != null
                      ? (portfolioRiskExposure.annualized_volatility > 25 ? 'High' : portfolioRiskExposure.annualized_volatility > 15 ? 'Moderate' : 'Low')
                      : null}
                    color={portfolioRiskExposure.annualized_volatility != null
                      ? (portfolioRiskExposure.annualized_volatility > 25 ? '#c62828' : portfolioRiskExposure.annualized_volatility > 15 ? '#f57c00' : '#2e7d32')
                      : undefined}
                    sourceKey="portfolioVol"
                    coverage={portfolioRiskExposure ? { count: portfolioRiskExposure.tickers_with_data, total: portfolioRiskExposure.tickers_total, coveragePct: portfolioRiskExposure.price_coverage_pct } : null}
                  />
                  <MetricCard
                    title="Exposure VaR 95%"
                    value={portfolioRiskExposure.var_95_1d_eur != null ? fmtCurrency(portfolioRiskExposure.var_95_1d_eur) : '—'}
                    subtitle={portfolioRiskExposure.var_95_1d_pct != null ? `${fmtPct(portfolioRiskExposure.var_95_1d_pct)} of covered` : null}
                    color="#e65100"
                    sourceKey="portfolioVar"
                  />
                  <MetricCard
                    title="Exposure Sharpe"
                    value={portfolioRiskExposure.portfolio_sharpe != null ? fmtNum(portfolioRiskExposure.portfolio_sharpe) : '—'}
                    subtitle={portfolioRiskExposure.portfolio_sharpe != null ? getSharpeLevel(portfolioRiskExposure.portfolio_sharpe).label : null}
                    color={portfolioRiskExposure.portfolio_sharpe != null ? getSharpeLevel(portfolioRiskExposure.portfolio_sharpe).color : undefined}
                    sourceKey="portfolioSharpe"
                  />
                  <MetricCard
                    title="Exposure Diversification"
                    value={portfolioRiskExposure.diversification_ratio != null ? `${fmtNum(portfolioRiskExposure.diversification_ratio)}×` : '—'}
                    subtitle={portfolioRiskExposure.diversification_ratio != null
                      ? (portfolioRiskExposure.diversification_ratio >= 1.5 ? 'Well diversified' : portfolioRiskExposure.diversification_ratio >= 1.2 ? 'Moderate' : 'Low diversification')
                      : null}
                    color={portfolioRiskExposure.diversification_ratio != null
                      ? (portfolioRiskExposure.diversification_ratio >= 1.5 ? '#2e7d32' : portfolioRiskExposure.diversification_ratio >= 1.2 ? '#f57c00' : '#c62828')
                      : undefined}
                    sourceKey="diversification"
                  />
                </Box>
              </>
            )}

            {/* Comparison with direct-only averages */}
            {risk && exposureRisk.beta.value != null && risk.beta.value != null && (
              <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: '#fffde7' }}>
                <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5, textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: 0.5 }}>
                  Direct vs Exposure comparison
                </Typography>
                <Box display="flex" gap={3} flexWrap="wrap">
                  {[
                    { label: 'Beta', direct: risk.beta.value, exposure: exposureRisk.beta.value, fmt: fmtNum },
                    { label: 'Sharpe', direct: risk.sharpe.value, exposure: exposureRisk.sharpe.value, fmt: fmtNum },
                    { label: 'P(Return+)', direct: risk.returnProb.value, exposure: exposureRisk.returnProb.value, fmt: (v) => fmtPct(v, 0) },
                    { label: 'Exp Q Ret', direct: risk.expectedReturn.value, exposure: exposureRisk.expectedReturn.value, fmt: (v) => fmtPct(v, 1) },
                  ].map(({ label, direct, exposure, fmt }) => (
                    <Box key={label} sx={{ minWidth: 100 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>{label}</Typography>
                      <Box display="flex" gap={1} alignItems="baseline">
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {direct != null ? fmt(direct) : '—'}
                        </Typography>
                        <Typography variant="caption" color="text.disabled">{'→'}</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {exposure != null ? fmt(exposure) : '—'}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
                <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
                  Left = direct holdings only ({risk.positions?.length || 0} tickers) · Right = full exposure ({exposureRisk.positions.length} tickers)
                </Typography>
              </Paper>
            )}

            {/* Exposure risk table */}
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1} flexWrap="wrap" gap={1}>
              <TextField
                size="small"
                placeholder="Search exposure ticker..."
                value={expSearch}
                onChange={(e) => setExpSearch(e.target.value)}
                sx={{ width: 280 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              <Typography variant="body2" color="text.secondary">
                {filteredExposures.length} of {exposureRisk.positions.length} exposure tickers
              </Typography>
            </Box>

            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: '45vh' }}>
              <MuiTable size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Ticker</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Holding</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      <TableSortLabel active={expOrderBy === 'beta'} direction={expOrderBy === 'beta' ? expOrder : 'desc'} onClick={() => handleExpSort('beta')}>
                        Beta
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      <TableSortLabel active={expOrderBy === 'sharpe'} direction={expOrderBy === 'sharpe' ? expOrder : 'desc'} onClick={() => handleExpSort('sharpe')}>
                        Sharpe
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      <TableSortLabel active={expOrderBy === 'returnProb'} direction={expOrderBy === 'returnProb' ? expOrder : 'desc'} onClick={() => handleExpSort('returnProb')}>
                        P(Ret+)
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      <Tooltip title="(a) Total economic exposure = direct + via ETF" arrow>
                        <TableSortLabel active={expOrderBy === 'totalExposure'} direction={expOrderBy === 'totalExposure' ? expOrder : 'desc'} onClick={() => handleExpSort('totalExposure')}>
                          (a) Total
                        </TableSortLabel>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      <Tooltip title="(b) Held directly in portfolio" arrow>
                        <TableSortLabel active={expOrderBy === 'stockExposure'} direction={expOrderBy === 'stockExposure' ? expOrder : 'desc'} onClick={() => handleExpSort('stockExposure')}>
                          (b) Direct
                        </TableSortLabel>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      <Tooltip title="(c) Held indirectly via ETFs/Funds" arrow>
                        <TableSortLabel active={expOrderBy === 'trackerExposure'} direction={expOrderBy === 'trackerExposure' ? expOrder : 'desc'} onClick={() => handleExpSort('trackerExposure')}>
                          (c) via ETF
                        </TableSortLabel>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      <TableSortLabel active={expOrderBy === 'weight'} direction={expOrderBy === 'weight' ? expOrder : 'desc'} onClick={() => handleExpSort('weight')}>
                        Weight
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredExposures.map((row, idx) => {
                    const ht = HOLDING_TYPE_CHIPS[row.holdingType] || HOLDING_TYPE_CHIPS.unknown;
                    const betaLvl = row.beta != null ? getBetaLevel(row.beta) : null;
                    const sharpeLvl = row.sharpe != null ? getSharpeLevel(row.sharpe) : null;
                    const probLvl = row.returnProb != null ? getProbLevel(row.returnProb) : null;
                    const dash = <Typography variant="caption" color="text.disabled">—</Typography>;

                    return (
                      <TableRow key={row.ticker || idx} hover>
                        <TableCell>
                          <ClickableTickerChip ticker={row.ticker} source={source} />
                          {row.company && (
                            <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.25 }}>
                              {row.company}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={ht.label}
                            size="small"
                            sx={{ fontSize: '0.55rem', height: 18, bgcolor: ht.bg, color: ht.color, fontWeight: 600 }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          {row.beta != null ? (
                            <Typography variant="body2" sx={{ color: betaLvl?.color, fontWeight: 500 }}>
                              {fmtNum(row.beta)}
                            </Typography>
                          ) : dash}
                        </TableCell>
                        <TableCell align="right">
                          {row.sharpe != null ? (
                            <Typography variant="body2" sx={{ color: sharpeLvl?.color, fontWeight: 500 }}>
                              {fmtNum(row.sharpe)}
                            </Typography>
                          ) : dash}
                        </TableCell>
                        <TableCell align="right">
                          {row.returnProb != null ? (
                            <Tooltip title={`Regime: ${row.regime || '?'} · Exp Q: ${row.expectedReturn != null ? fmtPct(row.expectedReturn) : '?'}`} arrow>
                              <Typography variant="body2" sx={{ color: probLvl?.color, fontWeight: 500 }}>
                                {fmtPct(row.returnProb, 0)}
                              </Typography>
                            </Tooltip>
                          ) : dash}
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {row.totalExposure > 0 ? fmtCurrency(row.totalExposure) : '—'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          {row.stockExposure > 0 ? (
                            <Typography variant="body2" sx={{ color: '#2e7d32' }}>
                              {fmtCurrency(row.stockExposure)}
                            </Typography>
                          ) : dash}
                        </TableCell>
                        <TableCell align="right">
                          {row.trackerExposure > 0 ? (
                            <Tooltip title={row.trackerDetails || 'Via ETF/Fund'} arrow placement="left">
                              <Typography variant="body2" sx={{ color: '#7b1fa2', cursor: 'help' }}>
                                {fmtCurrency(row.trackerExposure)}
                              </Typography>
                            </Tooltip>
                          ) : dash}
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">{fmtPct(row.weight * 100, 1)}</Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </MuiTable>
            </TableContainer>
          </Collapse>
        </>
      )}
    </Box>
  );
};

export default PAModal_Risk;
